<template>
  <div class="min-h-screen flex items-center justify-center">
    <div class="w-full px-4 sm:px-0 sm:max-w-md">
      <!-- Login Card  -->
      <div class="bg-white rounded-2xl overflow-hidden relative border shadow">
        <!-- Loading Overlay  -->
        <div
          v-if="isGoogleProcessing"
          class="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50 rounded-2xl"
        >
          <div class="text-center">
            <div
              class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"
            ></div>
            <p class="text-gray-600 text-sm">Authenticating with Google...</p>
          </div>
        </div>

        <!-- Header  -->
        <LoginHeader />

        <!-- Account Not Found Modal  -->
        <AccountNotFoundModal
          :show="showAccountNotFoundModal"
          :email="accountNotFoundEmail"
          @close="closeAccountNotFoundModal"
        />

        <!-- Body  -->
        <div class="px-8 pt-2 pb-8">
          <!-- <CHANGE> Keep Google OAuth DOM stable and reorder via CSS instead of v-if duplication -->
          <div class="flex flex-col gap-4">
            <!-- Forms Block (Password or OTP) -->
            <div :class="isOtpMode ? 'order-1' : 'order-2'">
              <!-- Password Login -->
              <LoginForm
                v-if="!isOtpMode"
                :error="errorMessage"
                :is-loading="isLoading"
                @submit="handleLogin"
                @update:error="errorMessage = $event"
              />

              <!-- OTP Login  -->
              <OtpLogin
                v-else
                @success="handleOtpSuccess"
                @cancel="isOtpMode = false"
              />

              <!-- Toggle Between Password / OTP  -->
              <div class="mt-3 text-center">
                <button
                  type="button"
                  class="text-sm text-blue-600 hover:text-blue-800 font-semibold hover:underline transition-colors"
                  @click="isOtpMode = !isOtpMode"
                >
                  {{ isOtpMode ? "Use password instead" : "Login with OTP" }}
                </button>
              </div>
            </div>

            <!-- Google OAuth Block (always mounted, reordered with order classes)  -->
            <div :class="isOtpMode ? 'order-2' : 'order-1'">
              <div class="space-y-3">
                <!-- When OTP mode is active and Google is below, show divider first  -->
                <template v-if="isOtpMode">
                  <Divider />
                </template>

                <!-- Stable Google placeholders (single instance, fixed IDs)  -->
                <div
                  id="g_id_onload"
                  class="mb-4"
                ></div>
                <div
                  id="g_id_signin"
                  class="w-full flex justify-center mb-4"
                ></div>

                <!-- When NOT in OTP mode (Google above), show divider after Google  -->
                <template v-if="!isOtpMode">
                  <Divider />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Signup link -->
      <div class="text-center mt-4">
        <RouterLink
          to="/signup"
          class="text-sm font-semibold text-indigo-600 hover:text-purple-600 transition-all"
        >
          Don’t have an account? Sign up
        </RouterLink>
      </div>
      <!-- Footer  -->
      <LoginFooter />
    </div>
  </div>
</template>
<script setup>
  import { ref, onMounted, onUnmounted } from "vue";
  import { useStore } from "vuex";
  import jwt_decode from "jwt-decode";
  import { RouterLink } from "vue-router";

  // Components
  import LoginHeader from "@/components/Auth/LoginHeader.vue";
  import AccountNotFoundModal from "@/components/Auth/AccountNotFoundModal.vue";
  import Divider from "@/components/Auth/Divider.vue";
  import LoginForm from "@/components/Auth/LoginForm.vue";
  import LoginFooter from "@/components/Auth/LoginFooter.vue";
  import OtpLogin from "@/components/Auth/OtpLogin.vue";

  // Composables
  import { useGoogleAuth } from "@/composables/useGoogleAuth";
  import { useAuth } from "@/composables/useAuth";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  const store = useStore();
  const { login, redirectToDashboard } = useAuth();
  const { showToast } = useGlobalToast();

  // eslint-disable-next-line no-unused-vars
  const {
    initializeGoogleAuth,
    // eslint-disable-next-line no-unused-vars
    handleGoogleLogin: googleLogin,
    cleanup,
  } = useGoogleAuth();

  // States
  const errorMessage = ref("");
  const isLoading = ref(false);
  const isGoogleProcessing = ref(false);
  const isOtpMode = ref(true);

  const showAccountNotFoundModal = ref(false);
  const accountNotFoundEmail = ref("");

  // --- Google OAuth Setup ---
  onMounted(async () => {
    await initializeGoogleAuth({
      onSuccess: handleGoogleSuccess,
      onError: (error, email) => handleGoogleError(error, email),
      onProcessingStart: () => {
        isGoogleProcessing.value = true;
      },
    });
  });

  onUnmounted(() => {
    cleanup();
  });

  // ✅ Handle successful Google authentication
  const handleGoogleSuccess = async (result) => {
    try {
      if (result.token) {
        await handleTokenLogin(result.token);
      }
    } finally {
      isGoogleProcessing.value = false;
    }
  };

  // ❌ Handle Google authentication errors
  const handleGoogleError = (error, email = null) => {
    console.error("Google authentication error:", error);

    if (error.response?.data?.error) {
      showToast("error", error.response.data.error);
    } else if (
      error.message?.includes("No account found with this email") ||
      error?.response?.status === 404
    ) {
      if (email) {
        accountNotFoundEmail.value = email;
        showAccountNotFoundModal.value = true;
      } else {
        showToast("error", "No account found. Please register first.");
      }
    } else {
      showToast("error", "Google authentication failed. Please try again.");
    }

    isGoogleProcessing.value = false;
  };

  // ✅ Handle Password Login
  const handleLogin = async (credentials) => {
    try {
      isLoading.value = true;
      errorMessage.value = ""; // reset error

      const result = await login(credentials);
      if (result?.token) {
        await handleTokenLogin(result.token);
      }
    } catch (err) {
      console.error("Login error:", err);

      if (
        err.response?.status === 404 &&
        err.response?.data?.error?.includes("No account found")
      ) {
        accountNotFoundEmail.value = credentials.email;
        showAccountNotFoundModal.value = true;
      } else {
        // Instead of toast, set the errorMessage
        errorMessage.value =
          err.response?.data?.error || "Login failed. Please try again.";
      }
    } finally {
      isLoading.value = false;
    }
  };

  // ✅ Handle OTP Login Success
  const handleOtpSuccess = async ({ token }) => {
    try {
      await handleTokenLogin(token);
    } catch (err) {
      console.error("[OTP] Post-login error:", err);
    }
  };

  // ✅ Shared Login Logic
  const handleTokenLogin = async (token) => {
    localStorage.setItem("token", token);
    const decoded = jwt_decode(token);

    if (decoded.schoolId) store.commit("SET_SCHOOL_ID", decoded.schoolId);
    if (decoded.role) store.commit("SET_USER_ROLE", decoded.role);
    if (decoded.userId) store.commit("SET_USER_ID", decoded.userId);
    if (decoded.name) store.commit("SET_USER_NAME", decoded.name);

    await store.dispatch("initializeApp");
    redirectToDashboard();
  };

  // Close "Account Not Found" modal
  const closeAccountNotFoundModal = () => {
    showAccountNotFoundModal.value = false;
    accountNotFoundEmail.value = "";
  };
</script>

<style scoped>
  * {
    transition-property: color, background-color, border-color,
      text-decoration-color, fill, stroke, opacity, box-shadow, transform,
      filter, backdrop-filter;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
  }

  /* Google One Tap customization */
  #g_id_onload {
    position: relative;
  }

  #g_id_signin {
    display: flex;
    justify-content: center;
    align-items: center;
  }
</style>
